import axios from 'axios';
import { NavLink } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useTheme } from "@/lib/useTheme";
import { usePermissions } from "@/lib/PermissionsContext";
import { useNotifications } from "@/lib/NotificationsContext";
import { useState, useEffect, useCallback, useRef } from "react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { reportClientEvent } from "@/lib/clientDiagnostics";

// 🆕 [2B.1] Petit composant qui affiche un bouton "Recharger" si le loading
// dépasse 15 secondes (le menu n'est probablement plus chargeable).
function SidebarLoadingTimeout({ retryFetch }) {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setStuck(true), 15000);
    return () => clearTimeout(id);
  }, []);
  if (!stuck) return null;
  return (
    <div className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900" data-testid="sidebar-loading-stuck">
      <p className="font-semibold mb-2">⏱ Chargement long</p>
      <p className="mb-2 text-[11px]">Votre connexion semble lente. Vous pouvez réessayer.</p>
      <button
        onClick={() => retryFetch && retryFetch()}
        className="w-full px-2 py-1 bg-amber-600 text-white rounded text-[11px] font-medium hover:bg-amber-700 transition-colors"
        data-testid="sidebar-loading-retry-btn"
      >
        🔄 Réessayer
      </button>
      <button
        onClick={() => window.location.reload()}
        className="w-full mt-1 px-2 py-1 bg-white border border-amber-300 text-amber-800 rounded text-[11px] hover:bg-amber-50 transition-colors"
      >
        Recharger la page
      </button>
    </div>
  );
}
import {
  LayoutDashboard, DollarSign, TrendingUp, Factory, Building2, Megaphone, Wallet, Banknote,
  Receipt, BarChart3, ChevronLeft, ChevronRight, ChevronDown, Settings, Clock, Bell, CreditCard, PieChart,
  Database, Tag, Coins, Warehouse, Trophy, User, CheckSquare, Target, ClipboardCheck, Users,
  Timer, ArrowRight, Percent, ShirtIcon, FolderKanban, MessageSquare, Calendar, Truck, Umbrella,
  Wrench, Package, HardHat, Landmark, Store, LogOut, AlertTriangle, Gift, BarChart2, Moon, Sun,
  FileSignature, Home, Shield, Lock, Calculator, Key, Brain, Lightbulb, Mail, Briefcase, AlertCircle, Send
} from "lucide-react";
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

export const Sidebar = ({ isOpen, setIsOpen }) => {
  const { user } = useAuth();
  const { canSeePage, canDo, fetchError, retryFetch, loading: permsLoading, permissions, degradedMode } = usePermissions() || {};
  const { badges } = useNotifications();

  // État des catégories dépliées/réduites (persisté dans localStorage)
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar-collapsed-v2');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  // Badge messagerie (messages non lus)
  const [unreadMessages, setUnreadMessages] = useState(0);
  // Badge installations non payées LMDS
  const [nonPayeesCount, setNonPayeesCount] = useState(0);
  // Badge paies non classifiées (Ventilation Paie)
  const [paiesAClasserCount, setPaiesAClasserCount] = useState(0);
  // Badge échecs notifications (Centre de Notifications unifié)
  const [notifFailuresCount, setNotifFailuresCount] = useState(0);
  const [notifActionsCount, setNotifActionsCount] = useState(0);
  // 🆕 2026-05-07 — Badge sync_failures (post-traitements échoués)
  const [syncFailuresCount, setSyncFailuresCount] = useState(0);
  const userId = user?.id || "";

  const fetchUnread = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await axios.get(`${window.__API_URL__ || ""}/api/messagerie/unread_count?user_id=${userId}`);
      if (r.status === 200) {
        const d = r.data;
        setUnreadMessages(d.unread_count || 0);
      }
    } catch { /* silencieux */ }
  }, [userId]);

  const fetchNonPayeesCount = useCallback(async () => {
    try {
      const isInst = aLeRole(user, 'installateur');
      const empId = user?.employee_id || '';
      const url = isInst && empId
        ? `${window.__API_URL__ || ""}/api/lmds/toutes-non-payees/count?installer_id=${empId}`
        : `${window.__API_URL__ || ""}/api/lmds/toutes-non-payees/count`;
      const r = await axios.get(url);
      if (r.status === 200) {
        const d = r.data;
        setNonPayeesCount(d.count || 0);
      }
    } catch { /* silencieux */ }
  }, [user]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    fetchNonPayeesCount();
    const interval = setInterval(fetchNonPayeesCount, 30000);
    return () => clearInterval(interval);
  }, [fetchNonPayeesCount]);

  // Fetch paies non classifiées count
  const fetchPaiesCount = useCallback(async () => {
    try {
      const r = await axios.get(`${window.__API_URL__ || ""}/api/payroll/unclassified-count`);
      if (r.status === 200) {
        const d = r.data;
        setPaiesAClasserCount(d.count || 0);
      }
    } catch { /* silencieux */ }
  }, []);
  useEffect(() => {
    fetchPaiesCount();
    const interval = setInterval(fetchPaiesCount, 60000);
    return () => clearInterval(interval);
  }, [fetchPaiesCount]);

  // Fetch notification failures count (Centre de Notifications unifié)
  const fetchNotifFailures = useCallback(async () => {
    if (!userId) return;
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const [failuresRes, badgesRes] = await Promise.all([
        axios.get(`${window.__API_URL__ || ""}/api/notifications/unread-failures`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${window.__API_URL__ || ""}/api/notifications/badges`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);
      if (failuresRes.status === 200) {
        const d = failuresRes.data;
        setNotifFailuresCount(d.count || 0);
      }
      if (badgesRes.status === 200) {
        const b = badgesRes.data;
        setNotifActionsCount((b.conges || 0) + (b.validation_heures || 0) + (b.avertissements || 0) + (b.todos || 0) + (b.incidents || 0));
      }
    } catch { /* silencieux */ }
  }, [userId]);

  useEffect(() => {
    fetchNotifFailures();
    const interval = setInterval(fetchNotifFailures, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifFailures]);

  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via `admin_sync_failures_voir`.
  //   Widget de compteur d'échecs de sync backend. Endpoint `/api/admin/sync-failures/*` est
  //   déjà lockdown Vague 2c (require_owner_or_roles(['charge_projet'])).
  const fetchSyncFailures = useCallback(async () => {
    if (!canDo || !canDo('admin_sync_failures_voir')) return;
    try {
      const r = await axios.get(`${window.__API_URL__ || ""}/api/admin/sync-failures/count`);
      if (r.status === 200) setSyncFailuresCount(r.data?.count || 0);
    } catch { /* silencieux */ }
  }, [canDo]);
  useEffect(() => {
    fetchSyncFailures();
    const interval = setInterval(fetchSyncFailures, 90000);
    return () => clearInterval(interval);
  }, [fetchSyncFailures]);

  const toggleGroup = (title) => {
    setCollapsed(prev => {
      const next = { ...prev, [title]: !prev[title] };
      localStorage.setItem('sidebar-collapsed-v2', JSON.stringify(next));
      return next;
    });
  };

  // Helper: filtre les items selon les permissions
  // Cat H — logique métier UI : marqueurs spéciaux (`__proprietaire_only__`, `__owner_only__`, `__parking_drivers__`)
  // ne passent pas par la matrice — ils incarnent des invariants métier (owner-only par nature, ou "tous authentifiés").
  // Le fail-secure Owner (ligne 195) reste car les permissions doivent être connues avant tout affichage → Cat H (identité).
  const visible = (items) => items.filter(item => {
    if (!item.pageKey) return true; // pas de restriction
    // 🆕 [2A.4] Marqueur spécial : item visible uniquement pour le propriétaire — Cat H (identité Owner)
    if (item.pageKey === '__proprietaire_only__') return estProprietaire(user);
    // 🛠️ [2026-06-12 v7] Marqueur Owner only pour Admin Parking — Cat H (identité Owner)
    if (item.pageKey === '__owner_only__') return estProprietaire(user);
    // 🅿️ [Phase 101 — 2026-07-09] Refonte Parking : visible pour tout user connecté.
    //     L'ancien check "permis de conduite" a été retiré — chauffeurs, chefs,
    //     compta, peintres, TOUS peuvent voir où sont assignées les unités.
    if (item.pageKey === '__parking_drivers__') {
      return true;
    }
    if (!canSeePage) return estProprietaire(user); // 🚨 [Phase 1.b Hotfix Sécurité — 2026-07-18] Fail-secure : sidebar cachée tant que permissions inconnues, sauf owner — Cat H (identité Owner)
    return canSeePage(item.pageKey);
  });

  const navGroups = [
    {
      title: "Tableau de bord",
      items: visible([
        { path: "/",        icon: LayoutDashboard, label: "Vue d'ensemble", pageKey: "vue_ensemble" },
        { path: "/alertes", icon: Bell,             label: "Alertes",        pageKey: "alertes" },
      ])
    },
    {
      title: "Dossiers clients",
      items: visible([
        { path: "/crm/clients",          icon: Building2,    label: "Clients",              pageKey: "clients" },
        { path: "/crm/dossiers",         icon: FolderKanban, label: "Tous les Projets",     pageKey: "projets" },
        { path: "/projets-envergure",    icon: FolderKanban, label: "Projets d'envergure",  pageKey: "projets_envergure" },
        { path: "/crm/relances-soumissions", icon: Send,     label: "Relances à approuver", pageKey: "relances_soumissions" },
      ])
    },
    {
      title: "Calendrier",
      items: visible([
        { path: "/crm/calendrier-rendez-vous", icon: Timer,    label: "Rendez-vous",       pageKey: "rendez_vous" },
        { path: "/calendrier-production",       icon: Calendar, label: "Production",         pageKey: "production" },
        { path: "/calendrier-equipements",      icon: Truck,    label: "Location",           pageKey: "location" },
        { path: "/routes-lavage",               icon: Wrench,   label: "Lavage",             pageKey: "lavage" },
        { path: "/conges",                      icon: Umbrella, label: "Congés & Vacances",  pageKey: "conges" },
      ])
    },
    {
      title: "Punch",
      items: visible([
        { path: "/pointage",          icon: Clock,        label: "Mon Pointage",          pageKey: "pointage" },
        { path: "/gestion-pointage",  icon: Users,        label: "Gestion des pointages", pageKey: "validation_heures" },
        { path: "/validation-heures", icon: CheckSquare,  label: "Validation des heures", pageKey: "validation_heures" },
        { path: "/rapport-heures",    icon: BarChart2,    label: "Rapport d'heures",      pageKey: "rapport_heures" },
      ])
    },
    {
      title: "RH & Opérations",
      items: visible([
        { path: "/mon-espace",             icon: Home,          label: "Mon Espace",         pageKey: "mon_espace" },
        // 🚨 [Phase 25 — 2026-05-31] BT Internes RESTAURÉ au sidebar — accès direct aux bons de travail internes
        // 🗑️ [Phase 33] BT Internes supprimé — migration manuelle terminée. Route /bt-internes désactivée.
        { path: "/inspection",             icon: ClipboardCheck,label: "Inspection",          pageKey: "inspection" },
        { path: "/todo-list",              icon: CheckSquare,   label: "To Do List",          pageKey: "todo_list" },
        { path: "/gestion-avertissements", icon: AlertTriangle, label: "Avertissements",      pageKey: "avertissements" },
        { path: "/gestion-bonus",          icon: Gift,          label: "Calcul des Bonus",    pageKey: "bonus" },
      ])
    },
    {
      title: "Communication",
      items: visible([
        { path: "/messagerie",             icon: MessageSquare, label: "Messagerie",          pageKey: "messagerie" },
        { path: "/propositions",           icon: Lightbulb,     label: "Propositions",        pageKey: "propositions" },
      ])
    },
    {
      title: "Stock",
      items: visible([
        { path: "/entrepot",           icon: Warehouse, label: "Entrepôt",            pageKey: "entrepot" },
        { path: "/gestion-produits",   icon: Tag,       label: "Produits",            pageKey: "produits" },
        { path: "/vehicules",          icon: Truck,     label: "Unités",              pageKey: "vehicules" },
        { path: "/services",           icon: Briefcase, label: "Services",            pageKey: "services" },
      ])
    },
    {
      // 🛠️ [2026-06-12 v7] Section Parking dédiée (avant : entrée isolée dans Stock)
      // — Places Unités : page principale unifiée (chefs + employés avec permis).
      //   🆕 [Phase 86 Sprint Parking 1.5 — 2026-06-23] L'ancienne entrée
      //   "Admin parking" a été fusionnée dans `/parking` via un toggle "Mode
      //   édition" Owner-only. La route `/parking/admin` reste pour rétrocompat
      //   bookmarks (elle redirige vers /parking?mode_edition=true).
      title: "🅿️ Parking",
      items: visible([
        { path: "/parking",          icon: Package,   label: "Places Unités",       pageKey: "__parking_drivers__" },
      ])
    },
    {
      title: "Performance",
      items: visible([
        { path: "/mon-bonus", icon: Trophy, label: "Mon Bonus", pageKey: "mon_bonus" },
      ])
    },
    {
      title: "Sous-traitants",
      items: visible([
        { path: "/sous-traitants/lmds",            icon: Store,        label: "Le Marché du Store",        pageKey: "lmds" },
        { path: "/sous-traitants/lmds-non-payees", icon: AlertTriangle, label: "Installations non payées",  pageKey: "lmds_non_payees" },
        { path: "/sous-traitants/lmds-facturation", icon: Receipt,      label: "Facturation Service Call",    pageKey: "lmds_facturation" },
        { path: "/sous-traitants/gestion-installateurs", icon: Settings, label: "Gestion des installateurs",  pageKey: "gestion_installateurs" },  // 🧾 Installateurs Phase 1
      ])
    },
    {
      title: "Analyses",
      items: visible([
        { path: "/revenus",       icon: DollarSign, label: "Analyse des revenus",  pageKey: "revenus" },
        { path: "/depenses",      icon: PieChart,   label: "Analyse des dépenses", pageKey: "depenses" },
        { path: "/rentabilite",   icon: TrendingUp, label: "Rentabilité",           pageKey: "rentabilite" },
        { path: "/analyse-marges",icon: Trophy,     label: "Marges par projet",     pageKey: "marges" },
        { path: "/production",    icon: Factory,    label: "Production",            pageKey: "structure" },
        { path: "/structure",     icon: Building2,  label: "Structure & Stabilité", pageKey: "structure" },
        { path: "/marketing",     icon: Megaphone,  label: "Ventes & Marketing",    pageKey: "marketing" },
        { path: "/comparaison",   icon: BarChart3,  label: "Comparaison",           pageKey: "comparaison" },
      ])
    },
    {
      title: "Trésorerie",
      items: visible([
        { path: "/cashflow",    icon: Wallet, label: "Cash Flow Bancaire",     pageKey: "cashflow" },
        { path: "/gestion-cash",icon: Coins,  label: "Argent Cash & Dépenses", pageKey: "gestion_cash" },
      ])
    },
    {
      title: "Valorisation",
      items: visible([
        { path: "/actifs-dettes",  icon: CreditCard, label: "Actifs / Dettes",    pageKey: "actifs_dettes" },
        { path: "/revente",        icon: Landmark,   label: "Revente",             pageKey: "revente" },
        { path: "/placements",     icon: PieChart,   label: "Placements",          pageKey: "placements" },
        { path: "/croissance-ia",  icon: Brain,      label: "Croissance avec IA",  pageKey: "croissance_ia" },
      ])
    },
    {
      title: "Données",
      items: visible([
        { path: "/transactions",           icon: Receipt,   label: "Transactions",       pageKey: "transactions" },
        { path: "/classification-manuelle",icon: Tag,       label: "Classification",     pageKey: "classification" },
        { path: "/ventilation-paie",       icon: Clock,     label: "Heures et paies",    pageKey: "heures_paies" },
        { path: "/import-historique",      icon: Database,  label: "Import Historique",  pageKey: "import_historique" },
        { path: "/mapping-categories-qb",  icon: ArrowRight,label: "Mapping Catégories QB", pageKey: "quickbooks" },
        { path: "/quickbooks",             icon: Database,  label: "QuickBooks",         pageKey: "quickbooks" },
      ])
    },
    {
      title: "Configuration Calculatrice",
      items: visible([
        { path: "/configuration-calcul",  icon: Calculator,    label: "Règles de calcul",          pageKey: "configuration" },
        { path: "/reglages/ccq",          icon: FileSignature, label: "Grille CCQ Peintres",       pageKey: "configuration" },
      ])
    },
    {
      title: "Configuration Employés",
      items: visible([
        { path: "/employes",                     icon: Users,         label: "Fiches Employés",           pageKey: "employes" },
        { path: "/gestion-utilisateurs",         icon: Shield,        label: "Comptes & Accès",           pageKey: "gestion_utilisateurs" },
        { path: "/gestion-acces",                icon: Key,           label: "Gestion des Permissions",   pageKey: "gestion_acces" },
        // 🆕 [2A.4] Diagnostics clients — propriétaire uniquement (filtré ci-dessous)
        { path: "/admin/diagnostics",            icon: AlertCircle,   label: "Diagnostics clients",        pageKey: "__proprietaire_only__" },
        { path: "/gestion-contrats",             icon: FileSignature, label: "Contrats de travail",       pageKey: "gestion_contrats" },
        { path: "/configuration-avertissements", icon: Bell,          label: "Règles Avertissements",     pageKey: "configuration" },
        { path: "/politiques-vetements",         icon: ShirtIcon,     label: "Politique Quotas",      pageKey: "politiques_vetements" },
        { path: "/configuration-mobile",         icon: LayoutDashboard, label: "Configuration Mobile",    pageKey: "configuration" },
      ])
    },
    {
      title: "Configuration Monétaire",
      items: visible([
        { path: "/configuration",         icon: Target,     label: "Cibles et seuils",   pageKey: "configuration" },
        { path: "/budgets",               icon: Settings,   label: "Budgets",             pageKey: "budgets" },
        { path: "/configuration-soldes",  icon: Wallet,     label: "Soldes initiaux",     pageKey: "budgets" },
        { path: "/gestion-ventilation",   icon: PieChart,   label: "Règles ventilation",  pageKey: "budgets" },
        { path: "/gestion-taxes",         icon: Percent,    label: "Règles Taxes",        pageKey: "budgets" },
      ])
    },
    {
      title: "Configuration AI",
      items: visible([
        { path: "/centre-notifications",    icon: Bell,          label: "Centre de Notifications", pageKey: "centre_notifications" },
        { path: "/messages-preenregistres", icon: MessageSquare, label: "Messages Templates",       pageKey: "messages" },
        { path: "/admin/sms-destinataires", icon: Send,          label: "Destinataires SMS auto",   pageKey: "configuration" },  // 🚀 [v13.55]
        // Historique SMS retiré — remplacé par Centre de Notifications
        { path: "/configuration-emails",    icon: Mail,          label: "Configuration Emails",     pageKey: "infos_entreprise" },
      ])
    },
    {
      title: "Configuration Légale",
      items: visible([
        { path: "/configuration-legal",     icon: Shield,        label: "Légal & Décharges",  pageKey: "configuration" },
      ])
    },
    {
      title: "Configuration Entreprise",
      items: visible([
        { path: "/securite",                icon: Lock,     label: "Sécurité & Sauvegarde",  pageKey: "securite" },
        { path: "/informations-entreprise", icon: Building2,label: "Infos Entreprise",         pageKey: "infos_entreprise" },
      ])
    },
  ].filter(group => group.items.length > 0);

  // 🆕 [2A.3] Détection : sidebar entièrement vide alors que user authentifié, non-prop, et permsLoading=false
  const sidebarEmptyReportedRef = useRef(false);
  useEffect(() => {
    if (!user || estProprietaire(user)) return;
    if (permsLoading) { sidebarEmptyReportedRef.current = false; return; }
    if (navGroups.length > 0) { sidebarEmptyReportedRef.current = false; return; }
    if (sidebarEmptyReportedRef.current) return;
    sidebarEmptyReportedRef.current = true;
    const pages = permissions?.pages || {};
    const keys = Object.keys(pages);
    const allFalse = keys.length > 0 && keys.every(k => pages[k] !== true);
    try {
      reportClientEvent('sidebar_empty_rendered', 'critical', {
        role: user?.role || '',
        permsHasPages: keys.length > 0,
        pagesAllFalse: allFalse,
        pagesKeyCount: keys.length,
        hasFetchError: !!fetchError,
        fetchErrorMsg: String(fetchError || '').slice(0, 200),
      });
    } catch { /* silencieux */ }
  }, [navGroups.length, permsLoading, user, permissions, fetchError]);

  // Mapping path → clé de badge
  const BADGE_MAP = {
    "/conges": badges.conges,
    "/calendrier-conges": badges.conges,
    "/validation-heures": badges.validation_heures,
    "/gestion-avertissements": badges.avertissements,
    "/todo-list": badges.todos,
    "/crm/relances-soumissions": badges.relances_soumissions,
    "/messagerie": unreadMessages,
    "/sous-traitants/lmds-non-payees": nonPayeesCount,
    "/ventilation-paie": paiesAClasserCount,
    "/centre-notifications": notifFailuresCount + notifActionsCount,
  };

  // Calcule le total des badges pour une catégorie (utile quand elle est réduite)
  const getCategoryBadge = (group) => {
    return group.items.reduce((sum, item) => sum + (BADGE_MAP[item.path] || 0), 0);
  };

  return (
    <aside
      data-testid="sidebar"
      className={`fixed left-0 top-0 h-full bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 z-50 noise-texture transition-all duration-300 ${isOpen ? 'w-64' : 'w-0 overflow-hidden'}`}
    >
      <div className="flex flex-col h-full">
        {/* Logo PRQC */}
        <div className="px-4 py-4 border-b border-slate-200">
          <img src={`${window.__API_URL__ || ""}/api/entreprise/logos/09ad526a-6f56-40fc-87e2-3e22ae048e44`} alt="Peinture Résidentielle QC" className="h-16 w-auto object-contain" 
            onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
          />
          <div className="w-11 h-11 bg-gradient-to-br from-[#1a237e] to-[#c62828] rounded-lg items-center justify-center shadow-sm hidden">
            <span className="text-white font-bold text-xl">P</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {/* 🆕 [2B.1] Skeleton de chargement explicite (au lieu d'une sidebar vide silencieuse pendant le fetch des permissions) */}
          {permsLoading && !estProprietaire(user) && (
            <div data-testid="sidebar-perms-loading" className="px-3 py-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-4 border-2 border-[#1a237e] border-t-transparent rounded-full animate-spin"></div>
                <p className="text-xs text-slate-500">Chargement de votre menu…</p>
              </div>
              {/* Skeleton items */}
              <div className="space-y-2">
                {[0, 1, 2, 3, 4].map(i => (
                  <div key={i} className="h-7 rounded-md bg-slate-200/60 animate-pulse" style={{ width: `${60 + (i % 3) * 15}%` }} />
                ))}
              </div>
              {/* Si > 15s : bouton recharger (géré via stuckReportedRef côté PermissionsContext) */}
              <SidebarLoadingTimeout retryFetch={retryFetch} />
            </div>
          )}

          {/* 🆕 2026-05-26 — Message visible si fetch permissions échoue (au lieu de sidebar vide silencieuse) */}
          {!permsLoading && fetchError && !estProprietaire(user) && (
            <div
              data-testid="sidebar-perms-error"
              className="mb-3 p-3 rounded-md bg-red-50 border border-red-300 text-xs text-red-800"
            >
              <p className="font-semibold mb-1">⚠️ Menu en cours de rechargement</p>
              <p className="mb-2 text-[11px]">
                Impossible de récupérer vos permissions ({fetchError}). 
                Le système réessaie automatiquement toutes les 30 secondes — votre menu reviendra dès que la connexion sera rétablie.
              </p>
              <button
                onClick={() => retryFetch && retryFetch()}
                className="w-full px-2 py-1 bg-red-600 text-white rounded text-[11px] font-medium hover:bg-red-700 transition-colors"
                data-testid="sidebar-perms-retry-btn"
              >
                🔄 Réessayer maintenant
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full mt-1 px-2 py-1 bg-white border border-red-300 text-red-700 rounded text-[11px] hover:bg-red-50 transition-colors"
                data-testid="sidebar-perms-reload-btn"
              >
                Recharger la page
              </button>
            </div>
          )}
          {navGroups.map((group) => {
            const isCollapsed = !!collapsed[group.title];
            return (
              <div key={group.title}>
                {/* En-tête de catégorie — cliquable pour plier/déplier */}
                <button
                  onClick={() => toggleGroup(group.title)}
                  className="w-full flex items-center justify-between px-3 py-1.5 rounded-md bg-[#1a237e]/[0.06] hover:bg-[#1a237e]/[0.12] transition-colors group mb-0.5"
                >
                  <p className="text-xs font-semibold text-[#1a237e]/60 uppercase tracking-wider group-hover:text-[#1a237e] transition-colors">
                    {group.title}
                  </p>
                  <div className="flex items-center gap-1.5">
                    {/* Badge agrégé : visible uniquement quand la catégorie est réduite */}
                    {isCollapsed && getCategoryBadge(group) > 0 && (
                      <span className="min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                        {getCategoryBadge(group) > 99 ? "99+" : getCategoryBadge(group)}
                      </span>
                    )}
                    <ChevronDown
                      className={`w-3 h-3 text-slate-300 group-hover:text-slate-500 transition-all duration-200 ${isCollapsed ? '-rotate-90' : ''}`}
                    />
                  </div>
                </button>

                {/* Items de la catégorie */}
                {!isCollapsed && (
                  <div className="space-y-0.5 mb-2">
                    {group.items.map((item) => {
                      const badgeCount = BADGE_MAP[item.path] || 0;
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-${item.path === '/' ? 'dashboard' : item.path.slice(1)}`}
                          onClick={() => { if (window.innerWidth < 768) setIsOpen(false); }}
                          className={({ isActive }) =>
                            `sidebar-link flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium ${isActive ? 'active' : 'text-slate-600'}`
                          }
                        >
                          <item.icon className="w-4 h-4 shrink-0" />
                          <span className="flex-1">{item.label}</span>
                          {badgeCount > 0 && (
                            <span className="ml-auto min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none" data-testid={`badge-${item.path.slice(1)}`}>
                              {badgeCount > 99 ? "99+" : badgeCount}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* 🆕 2026-05-07 — Widget discret de monitoring des sync échouées
            (visible uniquement quand il y a quelque chose à corriger) */}
        {syncFailuresCount > 0 && (
          <NavLink
            to="/admin/sync-failures"
            className="mx-3 mb-2 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors"
            data-testid="sidebar-sync-failures-widget"
            title="Synchronisations secondaires en échec — cliquer pour gérer"
          >
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 animate-pulse" />
            <span className="flex-1 truncate">Sync échouée{syncFailuresCount > 1 ? 's' : ''}</span>
            <span className="min-w-[18px] h-[18px] bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
              {syncFailuresCount > 99 ? '99+' : syncFailuresCount}
            </span>
          </NavLink>
        )}

        {/* Footer */}
        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-2 px-3 py-2">
            <UserIcon />
            <div className="flex-1">
              <UserInfo />
            </div>
            <LogoutButton />
          </div>
        </div>
      </div>
    </aside>
  );
};

export const SidebarToggle = ({ isOpen, setIsOpen }) => (
  <button
    data-testid="sidebar-toggle"
    onClick={(e) => {
      e.stopPropagation();
      setIsOpen(!isOpen);
    }}
    className={`fixed z-[100] bg-white border border-slate-200 shadow-md hover:bg-slate-50 transition-all duration-300 cursor-pointer
      top-4 rounded-lg p-2 sm:top-8 sm:rounded-full sm:p-1.5
      ${isOpen ? 'left-3 sm:left-[252px]' : 'left-3'}`}
    style={{ pointerEvents: 'auto' }}
  >
    {isOpen ? <ChevronLeft className="w-5 h-5 sm:w-4 sm:h-4 text-slate-600" /> : <ChevronRight className="w-5 h-5 sm:w-4 sm:h-4 text-slate-600" />}
  </button>
);


function UserIcon() {
  const { user } = useAuth();
  if (user?.photo_url) {
    return <EmployeeAvatar photoUrl={user.photo_url} prenom={user.prenom || ''} nom={user.nom || ''} size="sm" className="ring-0" />;
  }
  const initial = user?.prenom?.[0] || 'P';
  return <span className="text-emerald-900 font-semibold text-sm">{initial}</span>;
}

function UserInfo() {
  const { user } = useAuth();
  const roleLabels = { proprietaire: 'Propriétaire', charge_projet: 'Chargée de projet', chef_equipe: 'Chef d\'équipe' };
  return (
    <>
      <p className="text-sm font-medium text-slate-900">{user?.prenom} {user?.nom}</p>
      <p className="text-xs text-slate-500">{roleLabels[user?.role] || user?.role}</p>
    </>
  );
}

function LogoutButton() {
  const { logout } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  return (
    <div className="flex items-center gap-1">
      <button onClick={toggleTheme} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title={isDark ? "Mode clair" : "Mode sombre"} data-testid="sidebar-toggle-theme">
        {isDark ? <Sun className="w-4 h-4 text-amber-500" /> : <Moon className="w-4 h-4" />}
      </button>
      <button onClick={logout} className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors" title="Déconnexion" data-testid="logout-btn">
        <LogOut className="w-4 h-4" />
      </button>
    </div>
  );
}
